import { useEffect, useRef, useState, type ReactNode } from "react";
import { RedirectToSignIn, useAuth, useReverification, useUser } from "@clerk/react";
import { usePostHog } from "@posthog/react";
import { ApiError, MfaRequiredError } from "../../lib/api";
import {
  authMfaRequiredShown,
  authMfaReverifyCancelled,
  authMfaReverifyCompleted,
  captureAuthEvent,
} from "../../lib/auth-analytics";
import {
  freshSessionToken,
  mfaGate,
  useMfaGate,
  type MfaReverificationLevel,
  type MfaTrigger,
} from "../../lib/auth/mfa-gate";
import { hasSecondFactor } from "../../lib/auth/second-factor";
import { useProToken } from "../../hooks/useProToken";
import { useProfileStore } from "../../stores/profile";
import { IS_PREVIEW } from "../../lib/pro-mode";
import { NotAProScreen } from "./NotAProScreen";
import { SetupSecurityScreen } from "./SetupSecurityScreen";

/**
 * Front-door gate for the authenticated pro shell.
 *
 * Behavior (production / Clerk-key present):
 *  - Clerk still loading        → loading skeleton (no `useAuth` access before
 *                                 `isLoaded`, no flash of the authed shell).
 *  - loaded + signed out        → <RedirectToSignIn> → /login.
 *  - signed in                  → ACCESS PROBE via the SHARED profile store
 *                                 (`ensureLoaded`, SILL-346 item 3). The probe
 *                                 IS the app's one `/api/professional/profile`
 *                                 fetch — HeaderNav/Settings/Profile then read
 *                                 the same warm store instead of refetching. The
 *                                 store keeps the `ApiError` instance (not a
 *                                 flattened string), so the gate can branch on
 *                                 status:
 *                                   • loaded (2xx) → render the authed shell.
 *                                   • 403          → <NotAProScreen> (role isn't a pro).
 *                                   • 401          → <RedirectToSignIn> (token race /
 *                                                    expiry — force re-auth).
 *                                   • network/5xx  → retryable error state.
 *
 * Progressive MFA (spec 12.3): `lib/api.ts` raises a typed MfaRequiredError for
 * a 403 `mfa_required` from ANY request, including after `hasLoaded`. While the
 * gate is mounted it registers with `lib/auth/mfa-gate`, so the failing request
 * parks, the gate takes over the screen (enrol via /setup-security, or
 * reverify when a factor already exists), and the request retries once.
 *
 * Under IS_PREVIEW (no Clerk key, dev / /preview/*) the gate NO-OPS: it returns
 * children directly and never calls `useAuth` (which throws without a provider),
 * mirroring the hook discipline in `pro-mode.ts`. `IS_PREVIEW` is a module-level
 * constant, so the branch is stable across renders and the rules of hooks hold.
 */
export function ProAuthGate({ children }: { children: ReactNode }) {
  if (IS_PREVIEW) {
    return <>{children}</>;
  }
  return <AuthedGate>{children}</AuthedGate>;
}

function AuthedGate({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const getToken = useProToken();
  const error = useProfileStore((s) => s.error);
  const hasLoaded = useProfileStore((s) => s.hasLoaded);
  const mfa = useMfaGate();

  // Registered BEFORE the probe effect below so the very first probe's
  // mfa_required is parked here instead of surfacing as a bare error.
  useEffect(() => mfaGate.mount(), []);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    void useProfileStore.getState().ensureLoaded(getToken);
  }, [isLoaded, isSignedIn, getToken]);

  if (!isLoaded) {
    return <GateSkeleton />;
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  // The MFA branch outranks `hasLoaded`: a later request can re-enter it.
  if (mfa.status !== "idle" && mfa.trigger) {
    return (
      <MfaFlow key={mfa.episode} status={mfa.status} trigger={mfa.trigger} />
    );
  }

  // Once the probe has ever succeeded, the user is admitted — a later refresh
  // error (e.g. after a Profile edit) must not eject them from the shell.
  if (hasLoaded) {
    return <>{children}</>;
  }

  if (error instanceof MfaRequiredError) {
    // Reached only if the error escaped without a mounted gate; never the
    // not-a-pro screen (the account IS a pro).
    return (
      <ProbeError
        message="Two-step verification is required."
        onRetry={() => void useProfileStore.getState().refresh(getToken)}
      />
    );
  }
  if (error instanceof ApiError && error.status === 403) {
    return <NotAProScreen />;
  }
  if (error instanceof ApiError && error.status === 401) {
    // Signed-in Clerk session but the token didn't mint / has expired (refresh
    // race). Force re-auth instead of a retry screen — access is never granted
    // without a successful probe.
    return <RedirectToSignIn />;
  }
  if (error) {
    return (
      <ProbeError
        message={error.message || "Couldn't reach the server"}
        onRetry={() => void useProfileStore.getState().refresh(getToken)}
      />
    );
  }

  // Signed in, no result yet → still probing.
  return <GateSkeleton />;
}

/**
 * One run of the MFA remedy. The remedy is chosen ONCE per episode from the
 * Clerk user (I3): enrolling flips `totpEnabled` mid-flow, and re-deciding
 * would swap the screen away before the backup codes are saved.
 */
function MfaFlow({
  status,
  trigger,
}: {
  status: "required" | "blocked";
  trigger: MfaTrigger;
}) {
  const { user } = useUser();
  const getToken = useProToken();
  const posthog = usePostHog();
  const [remedy] = useState<"setup" | "reverify">(() =>
    hasSecondFactor(user) ? "reverify" : "setup",
  );

  // Once per episode: this component is keyed on the episode, and the ref
  // keeps a StrictMode double-effect from firing twice.
  const announced = useRef(false);
  useEffect(() => {
    if (announced.current) return;
    announced.current = true;
    captureAuthEvent(posthog, authMfaRequiredShown(remedy, trigger));
  }, [posthog, remedy, trigger]);

  // Fresh token: the session's second-factor age just changed.
  const reprobe = () =>
    void useProfileStore
      .getState()
      .refresh(async () => (await freshSessionToken()) ?? getToken());

  if (status === "blocked") {
    return (
      <MfaBlockedScreen
        onRetry={() => mfaGate.retry()}
        onNotNow={trigger === "acquisition" ? () => mfaGate.dismiss() : undefined}
      />
    );
  }
  if (remedy === "reverify") {
    return <ReverifyFlow onDone={reprobe} />;
  }

  return (
    <div className="relative">
      <SetupSecurityScreen
        trigger={trigger}
        onNotNow={trigger === "acquisition" ? () => mfaGate.dismiss() : undefined}
        onComplete={() => {
          // A parked request retries itself; otherwise re-probe.
          if (!mfaGate.settle(true)) reprobe();
        }}
      />
    </div>
  );
}

const DEFAULT_REVERIFICATION: MfaReverificationLevel = {
  level: "second_factor",
  afterMinutes: 10,
};

/**
 * Reverification via Clerk's own modal (I9, no `onNeedsReverification`
 * override). The wrapped fetcher first RETURNS the reverification hint carried
 * by the 403 (the backend's level drives the modal); Clerk opens the modal and
 * re-runs the fetcher, which then succeeds. Runs once per episode: cancelling
 * lands on the blocked state, never a re-probe loop.
 */
function ReverifyFlow({ onDone }: { onDone: () => void }) {
  const posthog = usePostHog();
  const level = useMfaGate((s) => s.reverification) ?? DEFAULT_REVERIFICATION;
  const armed = useRef(true);
  const started = useRef(false);
  const reverify = useReverification(async () => {
    if (armed.current) {
      armed.current = false;
      return {
        clerk_error: {
          type: "forbidden",
          reason: "reverification-error",
          metadata: { reverification: level },
        },
      };
    }
    return true;
  });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    reverify().then(
      () => {
        captureAuthEvent(posthog, authMfaReverifyCompleted());
        if (!mfaGate.settle(true)) onDone();
      },
      () => {
        captureAuthEvent(posthog, authMfaReverifyCancelled());
        mfaGate.settle(false);
      },
    );
  }, [reverify, onDone, posthog]);

  return <GateSkeleton />;
}

function MfaBlockedScreen({
  onRetry,
  onNotNow,
}: {
  onRetry: () => void;
  onNotNow?: () => void;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-warm p-6">
      <div className="max-w-[460px] text-center">
        <h1 className="view-h1 text-xl">Confirm it's you to continue</h1>
        <p className="view-sub mt-2">
          Your buyers' data is protected by 2-step verification. We couldn't
          confirm it just now.
        </p>
        <button type="button" className="ra-view mt-4" onClick={onRetry}>
          Try again
        </button>
        {onNotNow && (
          <button type="button" className="ra-view mt-4 ml-2" onClick={onNotNow}>
            Not now
          </button>
        )}
      </div>
    </div>
  );
}

function GateSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-warm">
      <div className="text-center">
        <div className="mx-auto mt-0 mb-2 size-8 animate-[spin_0.8s_linear_infinite] rounded-full border-2 border-[color-mix(in_oklch,var(--pro)_25%,transparent)] border-t-pro" />
        <p className="view-sub">Loading workspace…</p>
      </div>
    </div>
  );
}

function ProbeError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-warm p-6">
      <div className="max-w-[460px] text-center">
        <h1 className="view-h1 text-xl">
          Couldn't load your workspace
        </h1>
        <p className="view-sub mt-2">
          {message}
        </p>
        <button
          type="button"
          className="ra-view mt-4"
         
          onClick={onRetry}
        >
          Try again
        </button>
      </div>
    </div>
  );
}
